import { Head } from '@inertiajs/react'
import { useCallback, useMemo, useRef, useState } from 'react'

import LinedTextArea, {
  linedGutterWidthCh,
  LINED_LINE_NUMBERS_MAX_LINES,
  LINED_TEXT_AREA_FILL_CLASS,
} from '../../components/tools/LinedTextArea'
import ToolWorkspace from '../../components/tools/ToolWorkspace'
import { type ToolAbout, ToolIoChip, WrapLinesPanelSwitch } from '../../components/tools/toolChrome'
import { DIFF_TOOL_CODECS, getCodec, MAX_CODEC_INPUT_CHARS, unifiedTextDiff } from '../../lib/codecs'

const DIFF_ABOUT: ToolAbout = {
  description:
    'Compare a before text and an after text and get a unified line diff in patch style. Added, removed, and changed lines are listed for review. Nothing is uploaded. The work stays in your browser.',
  libraries: [{ name: 'jsdiff on GitHub', href: 'https://github.com/kpdecker/jsdiff' }],
}

const DIFF_CODEC_ID = DIFF_TOOL_CODECS[0].id

function diffLineClass(line: string): string {
  if (line.startsWith('--- ') || line.startsWith('+++ ')) return 'text-on-surface-variant'
  if (line.startsWith('@@')) return 'font-semibold text-primary-container'
  if (line.startsWith('-')) {
    return 'bg-[color-mix(in_srgb,var(--color-error)_14%,transparent)] text-[var(--color-error)]'
  }
  if (line.startsWith('+')) {
    return 'bg-emerald-500/14 text-emerald-800 dark:text-emerald-300'
  }
  if (line.startsWith(' ') || line.startsWith('\\')) return 'text-on-surface'
  return 'text-on-surface'
}

function DiffOutputPane({ text, wrapLines }: { text: string; wrapLines?: boolean }) {
  const lines = text.length === 0 ? [] : text.split('\n')
  const bodyRef = useRef<HTMLDivElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)

  const onScroll = useCallback(() => {
    const b = bodyRef.current
    const g = gutterRef.current
    if (b && g) g.scrollTop = b.scrollTop
  }, [])

  if (lines.length === 0) {
    return (
      <div
        className={`form-well grid-bg flex min-h-0 min-w-0 w-full items-center justify-center p-4 font-mono text-sm ${LINED_TEXT_AREA_FILL_CLASS}`}
      >
        <span className="text-on-surface-variant">Output appears here…</span>
      </div>
    )
  }

  const showGutter = lines.length <= LINED_LINE_NUMBERS_MAX_LINES
  const gutterCh = linedGutterWidthCh(lines.length)

  if (wrapLines) {
    return (
      <div
        className={`form-well grid-bg min-h-0 min-w-0 w-full overflow-y-auto overflow-x-hidden p-4 font-mono text-sm ${LINED_TEXT_AREA_FILL_CLASS}`}
        role="region"
        aria-label="Diff output"
      >
        {lines.map((line, i) => (
          <div
            key={i}
            className={`whitespace-pre-wrap break-words py-0.5 leading-6 ${diffLineClass(line)}`}
          >
            {line.length > 0 ? line : '\u00a0'}
          </div>
        ))}
      </div>
    )
  }

  if (!showGutter) {
    return (
      <div
        className={`form-well grid-bg min-h-0 min-w-0 w-full overflow-auto p-4 font-mono text-sm ${LINED_TEXT_AREA_FILL_CLASS}`}
        role="region"
        aria-label="Diff output"
      >
        <div className="w-max min-w-full">
          {lines.map((line, i) => (
            <div key={i} className={`whitespace-pre py-px pr-2 leading-6 ${diffLineClass(line)}`}>
              {line.length > 0 ? line : '\u00a0'}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`form-well grid-bg flex min-h-0 w-full min-w-0 overflow-hidden rounded-md ${LINED_TEXT_AREA_FILL_CLASS}`}
      role="region"
      aria-label="Diff output"
    >
      <div
        ref={gutterRef}
        className="box-border h-full min-h-0 shrink-0 select-none overflow-y-auto overflow-x-hidden border-r border-outline-variant/20 bg-surface-container-lowest/90 py-4 pl-2 pr-1.5 font-mono text-sm leading-6 text-on-surface-variant tabular-nums pointer-events-none"
        style={{ width: `${gutterCh}ch`, minWidth: '3rem' }}
        aria-hidden
      >
        {lines.map((_, i) => (
          <div key={i} className="h-6 text-right">
            {i + 1}
          </div>
        ))}
      </div>
      <div
        ref={bodyRef}
        onScroll={onScroll}
        className="h-full min-h-0 min-w-0 flex-1 overflow-auto py-4 pr-3 pl-3 font-mono text-sm"
      >
        <div className="w-max min-w-full">
          {lines.map((line, i) => (
            <div key={i} className={`min-h-6 whitespace-pre pr-2 leading-6 ${diffLineClass(line)}`}>
              {line.length > 0 ? line : '\u00a0'}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function DiffTool() {
  const codec = useMemo(() => getCodec(DIFF_CODEC_ID)!, [])
  const [mode, setMode] = useState<'encode' | 'decode'>('encode')
  const [input, setInput] = useState('')
  const [secondInput, setSecondInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lineWrap, setLineWrap] = useState(false)

  const run = useCallback(async () => {
    try {
      setError(null)
      if (input.length > MAX_CODEC_INPUT_CHARS || secondInput.length > MAX_CODEC_INPUT_CHARS) {
        setError(`Input exceeds ${MAX_CODEC_INPUT_CHARS.toLocaleString()} characters (browser safety limit).`)
        return
      }
      setBusy(true)
      setOutput(unifiedTextDiff(input, secondInput))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Diff failed')
    } finally {
      setBusy(false)
    }
  }, [input, secondInput])

  const copy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
    } catch {
      setError('Could not copy to clipboard')
    }
  }, [output])

  const swap = useCallback(() => {
    const a = input
    setInput(secondInput.slice(0, MAX_CODEC_INPUT_CHARS))
    setSecondInput(a.slice(0, MAX_CODEC_INPUT_CHARS))
    setOutput('')
    setError(null)
  }, [input, secondInput])

  const paneLabelClass =
    'pointer-events-none absolute top-2 right-3 z-10 max-w-[42%] truncate text-right font-mono text-[9px] font-semibold tracking-widest text-on-surface-variant uppercase'

  const inputColumn = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 lg:min-h-0">
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <span className={paneLabelClass} aria-hidden>
          Before
        </span>
        <LinedTextArea
          fillHeight
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, MAX_CODEC_INPUT_CHARS))}
          maxLength={MAX_CODEC_INPUT_CHARS}
          rows={8}
          wrapLines={lineWrap}
          placeholder={codec.placeholderEncode}
          aria-label="Diff left input"
        />
      </div>
      <div className="h-px shrink-0 bg-outline-variant/10" aria-hidden />
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <span className={paneLabelClass} aria-hidden>
          After
        </span>
        <LinedTextArea
          fillHeight
          value={secondInput}
          onChange={(e) => setSecondInput(e.target.value.slice(0, MAX_CODEC_INPUT_CHARS))}
          maxLength={MAX_CODEC_INPUT_CHARS}
          rows={8}
          wrapLines={lineWrap}
          placeholder={codec.placeholderSecond ?? ''}
          aria-label="Diff right input"
        />
      </div>
    </div>
  )

  const toolSettings = <WrapLinesPanelSwitch lineWrap={lineWrap} onLineWrapChange={setLineWrap} />

  return (
    <>
      <Head title="Text diff" />
      <ToolWorkspace
        heading="Unified diff (lines)"
        inputHeaderStart={<ToolIoChip>DIFF</ToolIoChip>}
        toolSettings={toolSettings}
        inputColumn={inputColumn}
        mode={mode}
        setMode={setMode}
        encodeOnly
        swapDisabled={false}
        swapTitle="Swap left and right"
        onSwap={swap}
        onExecute={run}
        executeBusy={busy}
        output={output}
        onCopy={copy}
        error={error}
        statsCharValue={`${String(input.length).padStart(6, '0')} / ${String(secondInput.length).padStart(6, '0')}`}
        statsAlgorithm="DIFF"
        statsOutputLen={String(output.length).padStart(6, '0')}
        statsModeLabel="DIFF"
        lineWrap={lineWrap}
        outputSlot={<DiffOutputPane text={output} wrapLines={lineWrap} />}
        about={DIFF_ABOUT}
      />
    </>
  )
}
